import { useEffect, useRef } from 'react';

interface UseKeyboardNavigationOptions {
  /**
   * Callback for Escape key
   */
  onEscape?: () => void;
  /**
   * Callback for Enter key (when no input is focused)
   */
  onEnter?: () => void;
  /**
   * Whether keyboard navigation is enabled
   */
  enabled?: boolean;
}

/**
 * Centralized keyboard navigation hook.
 * Handles Escape and Enter key events for the setup flow.
 */
export function useKeyboardNavigation({
  onEscape,
  onEnter,
  enabled = true,
}: UseKeyboardNavigationOptions) {
  const lastClickedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      lastClickedRef.current = event.target as HTMLElement;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onEscape) {
        const last = lastClickedRef.current;
        const isInsideFullViewportHeight =
          last && last.closest('.full-viewport-height');

        if (isInsideFullViewportHeight || !lastClickedRef.current) {
          onEscape();
        }
      }

      if (event.key === 'Enter' && onEnter) {
        const last = lastClickedRef.current;
        const isInsideFullViewportHeight =
          last && last.closest('.full-viewport-height');
        const isInputFocused =
          document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA';

        // Only trigger if inside viewport and no input is focused
        if (
          (isInsideFullViewportHeight || !lastClickedRef.current) &&
          !isInputFocused
        ) {
          onEnter();
        }
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, onEscape, onEnter]);
}
